import { useState } from "react";
import { useModal } from "react-modal-hook";
import { useDeferredPromise } from "../../../../../../../hooks/useDeferredPromise";
import { spacing } from "../../../../../../../stitches.config";
import { FlexColumn } from "../../../../../../Common/__styles__/Layout";
import {
  ButtonSection,
  Container,
  ContentSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../../../../../../Common/__styles__/Modal";
import { Button } from "../../../../../../Common/Buttons/Button";
import Modal from "../../../../../../Common/Modal";
import { Body, Title } from "../../../../../../Common/Typography";
import { Radio } from "../../../../../../Inputs";
import Checkbox from "../../../../../../Inputs/Checkbox";

export type PromoteSubmissionsModalOptions = {
  hasBreakingChanges: boolean;
  willRemovePublicAccess: boolean;
  hasPublicPortal: boolean;
};

export function useConfirmPromoteSubmissions() {
  const { defer, deferRef } = useDeferredPromise<"cancel" | boolean>();
  const [modalOptions, setModalOptions] =
    useState<PromoteSubmissionsModalOptions>({
      hasBreakingChanges: false,
      willRemovePublicAccess: false,
      hasPublicPortal: false,
    });

  const [_showModal, hideModal] = useModal(() => {
    function onCancel() {
      deferRef.current?.resolve("cancel");
      hideModal();
    }

    return (
      <Modal onRequestClose={onCancel}>
        <PromoteSubmissionsModal
          hasBreakingChanges={modalOptions.hasBreakingChanges}
          willRemovePublicAccess={modalOptions.willRemovePublicAccess}
          hasPublicPortal={modalOptions.hasPublicPortal}
          onSubmit={response => {
            deferRef.current?.resolve(response);
            hideModal();
          }}
          onCancel={onCancel}
        />
      </Modal>
    );
  }, [modalOptions]);

  function showModal(options: PromoteSubmissionsModalOptions) {
    setModalOptions(options);
    _showModal();

    return defer().promise;
  }

  return [showModal, hideModal] as const;
}

type PromoteSubmissionsModalProps = {
  hasBreakingChanges: boolean;
  willRemovePublicAccess: boolean;
  hasPublicPortal: boolean;
  onSubmit: (promoteSubmissions: boolean) => void;
  onCancel: () => void;
};

function PromoteSubmissionsModal({
  hasBreakingChanges,
  willRemovePublicAccess,
  hasPublicPortal,
  onSubmit,
  onCancel,
}: PromoteSubmissionsModalProps) {
  const [shouldPromoteSubmissions, setShouldPromoteSubmissions] =
    useState<string>("false");
  const [confirmHighlightsRemoval, setConfirmHighlightsRemoval] =
    useState(false);

  const showHighlightsWarning = hasPublicPortal && willRemovePublicAccess;
  const isSubmitDisabled = showHighlightsWarning && !confirmHighlightsRemoval;

  return (
    <Container overflows>
      <HeaderSection>
        <Title size="small" type="regular">
          Update existing records
        </Title>
      </HeaderSection>
      <ContentSection overflows css={{ height: "100%" }}>
        {hasBreakingChanges ?
          <Body size="default" type="regular">
            You've added one or more required fields. Because existing records
            may not have data for these, we can't safely update them. To apply
            changes only to new records moving forward, update below. To update
            existing records, cancel and make all fields optional.
          </Body>
        : <>
            <Body size="default" type="regular">
              You've made updates to this record type. Should these changes be
              updated on all existing records, or only to new ones going
              forward?
            </Body>
            <InputRow>
              <Radio
                name="promoteSubmissions"
                options={[
                  {
                    value: "true",
                    text: "Update existing and new records",
                  },
                  { value: "false", text: "Only update new records" },
                ]}
                value={shouldPromoteSubmissions}
                onChange={value =>
                  setShouldPromoteSubmissions(value ?? "false")
                }
              />
            </InputRow>
          </>
        }
        {showHighlightsWarning && (
          <FlexColumn gap="xs" style={{ marginTop: spacing.m.value }}>
            <Body size="default" type="regular">
              By changing who can fill out this form, you confirm that:
            </Body>
            <Checkbox
              value={confirmHighlightsRemoval}
              onChange={setConfirmHighlightsRemoval}
              label="Any highlights on your public website homepage that link to this form will be removed."
              required
            />
          </FlexColumn>
        )}
      </ContentSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            onPress={() => {
              onCancel();
            }}
            size="medium"
            variant="secondary"
            title="Cancel"
          />
          <Button
            onPress={() => {
              onSubmit(shouldPromoteSubmissions === "true");
            }}
            size="medium"
            variant="primary"
            type="button"
            title="Update"
            isDisabled={isSubmitDisabled}
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
}
